/**
 * Activity Feed Component
 * 
 * Real-time activity feed for the Parts Catalogue page.
 * Shows create, edit, delete, and restore actions with:
 * - Grouped by time (Today, Yesterday, This Week, Older)
 * - Click to navigate to part
 * - Real-time updates via WebSocket (instant updates)
 * - Fallback polling every 30 seconds
 * - Infinite scroll with "Load More"
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Plus, Pencil, Trash2, RotateCcw, Clock, 
  ChevronDown, Loader2, RefreshCw, User,
  ArrowRight, Package, Wifi, WifiOff
} from 'lucide-react';
import { cn } from '../lib/utils';
import { usePartDrawer } from '../hooks/usePartDrawer';
import { formatTimeAgoIST, formatISTDate, parseAsUTC } from '../utils/timezone';
import { useWebSocket } from '../contexts/WebSocketContext';
import api from '../services/api';

// Action configuration with icons and colors
// Support both frontend names (part_created) and backend names (CREATE)
const ACTION_CONFIG = {
  // Frontend naming
  part_created: {
    icon: Plus,
    label: 'created',
    bgColor: 'bg-green-50',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    borderColor: 'border-green-200'
  },
  part_updated: {
    icon: Pencil,
    label: 'edited',
    bgColor: 'bg-blue-50',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    borderColor: 'border-blue-200'
  },
  part_deleted: {
    icon: Trash2,
    label: 'archived',
    bgColor: 'bg-red-50',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    borderColor: 'border-red-200'
  },
  part_restored: {
    icon: RotateCcw,
    label: 'restored',
    bgColor: 'bg-amber-50',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    borderColor: 'border-amber-200'
  },
  // Backend naming (uppercase)
  CREATE: {
    icon: Plus,
    label: 'created',
    bgColor: 'bg-green-50',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    borderColor: 'border-green-200'
  },
  UPDATE: {
    icon: Pencil,
    label: 'edited',
    bgColor: 'bg-blue-50',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    borderColor: 'border-blue-200'
  },
  DELETE: {
    icon: Trash2,
    label: 'archived',
    bgColor: 'bg-red-50',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    borderColor: 'border-red-200'
  },
  RESTORE: {
    icon: RotateCcw,
    label: 'restored',
    bgColor: 'bg-amber-50',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    borderColor: 'border-amber-200'
  }
};

/**
 * Group activities by time period
 */
const groupActivitiesByTime = (activities) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const thisWeek = new Date(today);
  thisWeek.setDate(thisWeek.getDate() - 7);
  
  const groups = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: []
  };
  
  activities.forEach(activity => {
    const activityDate = parseAsUTC(activity.timestamp);
    if (!activityDate) {
      groups.older.push(activity);
      return;
    }
    
    if (activityDate >= today) {
      groups.today.push(activity);
    } else if (activityDate >= yesterday) {
      groups.yesterday.push(activity);
    } else if (activityDate >= thisWeek) {
      groups.thisWeek.push(activity);
    } else {
      groups.older.push(activity);
    }
  });
  
  return groups;
};

/**
 * Time Group Header
 */
const TimeGroupHeader = ({ label, count }) => (
  <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100 sticky top-0 z-10">
    <div className="w-2 h-2 rounded-full bg-haden-primary" />
    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
      {label}
    </span>
    <span className="text-xs text-slate-400">({count})</span>
  </div>
);

/**
 * Single Activity Item
 */
const ActivityItem = ({ activity, onNavigate, isNew }) => {
  const config = ACTION_CONFIG[activity.action] || ACTION_CONFIG.part_updated;
  const Icon = config.icon;
  
  const handleClick = () => {
    if (activity.hpn_compact && activity.action !== 'part_deleted') {
      onNavigate(activity.hpn_compact);
    }
  };
  
  const isClickable = activity.hpn_compact && activity.action !== 'part_deleted';
  
  return (
    <div
      onClick={handleClick}
      className={cn(
        "group px-3 py-2.5 border-b border-slate-100 transition-all duration-200",
        isClickable && "cursor-pointer hover:bg-slate-50",
        isNew && "animate-pulse-once bg-green-50/50"
      )}
      data-testid={`activity-item-${activity.event_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn(
          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
          config.iconBg
        )}>
          <Icon className={cn("w-4 h-4", config.iconColor)} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Actor and Action */}
          <div className="flex items-center gap-1.5 text-sm">
            <span className="font-medium text-slate-700 truncate max-w-[100px]">
              {activity.actor_name || 'System'}
            </span>
            <span className="text-slate-500">{config.label}</span>
            <span className="text-slate-500">part</span>
          </div>
          
          {/* Part Number */}
          <div className="mt-0.5 flex items-center gap-1.5">
            <Package className="w-3 h-3 text-slate-400" />
            <span className={cn(
              "text-sm font-mono",
              isClickable ? "text-haden-primary group-hover:underline" : "text-slate-500"
            )}>
              {activity.hpn_display || activity.hpn_compact || activity.part_id || 'Unknown'}
            </span>
          </div>
          
          {/* Description or Changes */}
          {(activity.description || activity.changes_summary || activity.reason_text) && (
            <p className="mt-0.5 text-xs text-slate-500 truncate">
              {(activity.action === 'part_deleted' || activity.action === 'DELETE') && activity.reason_text 
                ? `Reason: ${activity.reason_text}`
                : activity.changes_summary 
                  ? `Changed: ${activity.changes_summary}`
                  : activity.description}
            </p>
          )}
          
          {/* Timestamp */}
          <div className="mt-1 flex items-center gap-1 text-xs text-slate-400">
            <Clock className="w-3 h-3" />
            <span>{formatTimeAgoIST(activity.timestamp)}</span>
          </div>
        </div>
        
        {/* Arrow indicator for clickable items */}
        {isClickable && (
          <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-haden-primary transition-colors opacity-0 group-hover:opacity-100" />
        )}
      </div>
    </div>
  );
};

/**
 * Activity Feed Filter Bar
 */
const FilterBar = ({ activeFilter, onFilterChange }) => {
  const filters = [
    { value: null, label: 'All' },
    { value: 'CREATE', label: 'Created' },
    { value: 'UPDATE', label: 'Edited' },
    { value: 'DELETE', label: 'Archived' }
  ];
  
  return (
    <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-200 bg-white">
      {filters.map(filter => (
        <button
          key={filter.value || 'all'}
          onClick={() => onFilterChange(filter.value)}
          className={cn(
            "px-2.5 py-1 text-xs font-medium rounded-full transition-colors",
            activeFilter === filter.value
              ? "bg-haden-primary text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          )}
          data-testid={`activity-filter-${filter.value || 'all'}`}
        >
          {filter.label}
        </button>
      ))}
    </div>
  );
};

/**
 * Loading Skeleton
 */
const ActivitySkeleton = () => (
  <div className="px-3 py-2.5 border-b border-slate-100 animate-pulse">
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-slate-200" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-slate-200 rounded w-3/4" />
        <div className="h-3 bg-slate-200 rounded w-1/2" />
        <div className="h-3 bg-slate-200 rounded w-1/4" />
      </div>
    </div>
  </div>
);

/**
 * Empty State
 */
const EmptyState = ({ filter }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
      <Clock className="w-6 h-6 text-slate-400" />
    </div>
    <p className="text-sm font-medium text-slate-600">No activity yet</p>
    <p className="text-xs text-slate-400 mt-1">
      {filter 
        ? `No ${ACTION_CONFIG[filter]?.label || ''} activities found`
        : 'Part activities will appear here'}
    </p>
  </div>
);

/**
 * Main Activity Feed Component
 */
export const ActivityFeed = ({ className }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState(null);
  const [newActivityIds, setNewActivityIds] = useState(new Set());
  
  const { openDrawer } = usePartDrawer();
  const containerRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  
  // WebSocket for real-time updates
  const { subscribe, isConnected } = useWebSocket();
  
  // Fetch activities
  const fetchActivities = useCallback(async (pageNum = 1, append = false, silent = false) => {
    try {
      if (pageNum === 1 && !silent) {
        setLoading(true);
      } else if (pageNum > 1) {
        setLoadingMore(true);
      }
      setError(null);
      
      const params = new URLSearchParams();
      params.set('page', pageNum.toString());
      params.set('page_size', '20');
      if (filter) {
        params.set('action', filter);
      }
      
      const response = await api.get(`/api/v1/audit/v2/activity-feed?${params.toString()}`);
      const data = response.data;
      
      if (append) {
        setActivities(prev => [...prev, ...data.items]);
      } else {
        // Check for new items (for animation)
        if (activities.length > 0 && data.items.length > 0) {
          const existingIds = new Set(activities.map(a => a.event_id));
          const newIds = data.items
            .filter(item => !existingIds.has(item.event_id))
            .map(item => item.event_id);
          
          if (newIds.length > 0) {
            setNewActivityIds(new Set(newIds));
            // Clear "new" animation after 3 seconds
            setTimeout(() => setNewActivityIds(new Set()), 3000);
          }
        }
        setActivities(data.items);
      }
      
      setHasMore(data.has_more);
      setPage(pageNum);
      
    } catch (err) {
      console.error('Failed to fetch activity feed:', err);
      if (!silent) {
        setError('Failed to load activities');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filter, activities]);
  
  // Initial load
  useEffect(() => {
    fetchActivities(1, false);
  }, [filter]);
  
  // WebSocket real-time updates - subscribe to part events
  useEffect(() => {
    const handlePartEvent = (event) => {
      console.log('Activity Feed: WebSocket event received', event.event_type);
      // Refresh the feed when a part event occurs
      // Use silent=true to avoid showing loading spinner
      fetchActivities(1, false, true);
    };
    
    // Subscribe to all part events
    const unsubscribes = [
      subscribe('part.created', handlePartEvent),
      subscribe('part.updated', handlePartEvent),
      subscribe('part.deleted', handlePartEvent),
      subscribe('part.restored', handlePartEvent),
    ];
    
    return () => {
      unsubscribes.forEach(unsub => unsub && unsub());
    };
  }, [subscribe, fetchActivities]);
  
  // Fallback polling (only when WebSocket is disconnected, every 60 seconds)
  useEffect(() => {
    // Only poll if WebSocket is not connected
    if (!isConnected) {
      pollingIntervalRef.current = setInterval(() => {
        if (!loading && !loadingMore) {
          fetchActivities(1, false, true);
        }
      }, 60000);
    }
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isConnected, loading, loadingMore]);
  
  // Load more handler
  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchActivities(page + 1, true);
    }
  };
  
  // Manual refresh
  const handleRefresh = () => {
    setPage(1);
    fetchActivities(1, false);
  };
  
  // Filter change
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1);
    setActivities([]);
  };
  
  // Navigate to part
  const handleNavigate = (hpnCompact) => {
    openDrawer(hpnCompact);
  };
  
  // Group activities by time
  const groupedActivities = groupActivitiesByTime(activities);
  const hasActivities = activities.length > 0;
  
  return (
    <div 
      ref={containerRef}
      className={cn("flex flex-col h-full bg-white", className)}
      data-testid="activity-feed-panel"
    >
      {/* Compact sub-header - no redundant title since tab already shows "Activity" */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {/* WebSocket connection indicator */}
          <span 
            className={cn(
              "flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium",
              isConnected 
                ? "bg-green-50 text-green-600" 
                : "bg-slate-100 text-slate-400"
            )}
            title={isConnected ? "Real-time updates active" : "Using polling (every 60s)"}
          >
            {isConnected ? (
              <><Wifi className="w-2.5 h-2.5" /> Live</>
            ) : (
              <><WifiOff className="w-2.5 h-2.5" /> Polling</>
            )}
          </span>
          {activities.length > 0 && (
            <span className="px-2 py-0.5 bg-haden-primary/10 text-haden-primary font-medium rounded-full">
              {activities.length} events
            </span>
          )}
          {loading && (
            <span className="w-3 h-3 border-2 border-haden-primary border-t-transparent rounded-full animate-spin" />
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="p-1.5 rounded-md hover:bg-slate-100 transition-colors disabled:opacity-50"
          title="Refresh"
          data-testid="activity-refresh-btn"
        >
          <RefreshCw className={cn("w-4 h-4 text-slate-400", loading && "animate-spin")} />
        </button>
      </div>
      
      {/* Filter Bar */}
      <FilterBar activeFilter={filter} onFilterChange={handleFilterChange} />
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading && activities.length === 0 ? (
          // Initial loading
          <>
            <ActivitySkeleton />
            <ActivitySkeleton />
            <ActivitySkeleton />
            <ActivitySkeleton />
            <ActivitySkeleton />
          </>
        ) : error ? (
          // Error state
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <p className="text-sm text-red-500 mb-2">{error}</p>
            <button
              onClick={handleRefresh}
              className="text-xs text-haden-primary hover:underline"
            >
              Try again
            </button>
          </div>
        ) : !hasActivities ? (
          // Empty state
          <EmptyState filter={filter} />
        ) : (
          // Activity list grouped by time
          <>
            {/* Today */}
            {groupedActivities.today.length > 0 && (
              <>
                <TimeGroupHeader label="Today" count={groupedActivities.today.length} />
                {groupedActivities.today.map(activity => (
                  <ActivityItem
                    key={activity.event_id}
                    activity={activity}
                    onNavigate={handleNavigate}
                    isNew={newActivityIds.has(activity.event_id)}
                  />
                ))}
              </>
            )}
            
            {/* Yesterday */}
            {groupedActivities.yesterday.length > 0 && (
              <>
                <TimeGroupHeader label="Yesterday" count={groupedActivities.yesterday.length} />
                {groupedActivities.yesterday.map(activity => (
                  <ActivityItem
                    key={activity.event_id}
                    activity={activity}
                    onNavigate={handleNavigate}
                    isNew={newActivityIds.has(activity.event_id)}
                  />
                ))}
              </>
            )}
            
            {/* This Week */}
            {groupedActivities.thisWeek.length > 0 && (
              <>
                <TimeGroupHeader label="This Week" count={groupedActivities.thisWeek.length} />
                {groupedActivities.thisWeek.map(activity => (
                  <ActivityItem
                    key={activity.event_id}
                    activity={activity}
                    onNavigate={handleNavigate}
                    isNew={newActivityIds.has(activity.event_id)}
                  />
                ))}
              </>
            )}
            
            {/* Older */}
            {groupedActivities.older.length > 0 && (
              <>
                <TimeGroupHeader label="Older" count={groupedActivities.older.length} />
                {groupedActivities.older.map(activity => (
                  <ActivityItem
                    key={activity.event_id}
                    activity={activity}
                    onNavigate={handleNavigate}
                    isNew={newActivityIds.has(activity.event_id)}
                  />
                ))}
              </>
            )}
            
            {/* Load More */}
            {hasMore && (
              <div className="p-3 border-t border-slate-100">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="w-full py-2 px-4 text-sm font-medium text-haden-primary bg-haden-primary/5 rounded-lg hover:bg-haden-primary/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="activity-load-more-btn"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Load More
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;
